import React, { useEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { gql, useMutation, useQuery } from '@apollo/client';
import { flashMessage } from '~/app/actions';
import { S, A, TONES, DARK } from '~/app/ui/tokens';
import { tr } from '~/app/ui/lang';
import Button from '~/app/ui/Button';
import { Page, PageHeader, Section, Card } from '~/app/ui/Page';
import EmptyState from '~/app/ui/EmptyState';
import HouseStyleEditor from './HouseStyleEditor';

const SETTINGS = gql`
  query getSetting {
    settings {
      key
      label
      value
    }
  }
`;
const UPDATE = gql`
  mutation updateSettings($settings: [SettingInput]) {
    updateSettings(settings: $settings) {
      key
    }
  }
`;

type Setting = { key: string; label?: string | null; value?: string | null };

const useStyles = createUseStyles({
  list: { display: 'grid', gap: 0, padding: 0 },
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(180px, 280px) minmax(0, 1fr)',
    gap: '6px 24px',
    alignItems: 'start',
    padding: '14px 0',
    borderBottom: `1px solid ${A.rowLine}`,
    '&:last-child': { borderBottom: 0 },
    '@media (max-width: 720px)': { gridTemplateColumns: 'minmax(0, 1fr)' }
  },
  label: { fontSize: 14, fontWeight: 600, paddingTop: 9 },
  key: { display: 'block', marginTop: 2, fontFamily: S.mono, fontSize: 11, fontWeight: 400, color: S.muted },
  input: {
    boxSizing: 'border-box',
    width: '100%',
    minHeight: 38,
    padding: '8px 12px',
    border: `1px solid ${S.fieldLine}`,
    borderRadius: A.radius,
    fontFamily: S.mono,
    fontSize: 13,
    lineHeight: 1.45,
    color: S.fg,
    resize: 'vertical',
    '&:focus': { outline: 'none', borderColor: S.fg, boxShadow: `0 0 0 1px ${S.fg}` },
    [DARK]: { background: S.field }
  },
  changed: { borderColor: S.amber, background: 'rgba(176, 114, 10, 0.04)', [DARK]: { background: TONES.amber.bg } },
  saveBar: {
    position: 'sticky',
    bottom: 16,
    zIndex: 5,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginTop: 16,
    padding: '10px 12px 10px 16px',
    borderRadius: 12,
    background: S.fg,
    color: S.onFg,
    fontSize: 14,
    boxShadow: '0 12px 32px rgba(23, 26, 32, 0.24)'
  },
  saveText: { flex: 1 },
  // Same shape and size as Save, outlined, so the two read as a pair on the dark bar.
  ghost: {
    all: 'unset',
    boxSizing: 'border-box',
    padding: '7px 14px',
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.35)',
    color: S.onFg,
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': { background: 'rgba(255,255,255,0.12)', borderColor: 'rgba(255,255,255,0.6)' },
    '&:focus-visible': { outline: `2px solid ${S.onFg}`, outlineOffset: 2 },
    '&[disabled]': { opacity: 0.6, cursor: 'default' },
    [DARK]: { borderColor: S.muted, '&:hover': { background: S.fgActive, borderColor: S.fieldLine } }
  },
  light: {
    all: 'unset',
    boxSizing: 'border-box',
    padding: '7px 14px',
    borderRadius: 8,
    border: `1px solid ${S.onFg}`,
    background: S.onFg,
    color: S.fg,
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': { background: '#E9EBEE' },
    '&:focus-visible': { outline: `2px solid ${S.onFg}`, outlineOffset: 2 },
    '&[disabled]': { opacity: 0.6, cursor: 'default' },
    [DARK]: { '&:hover': { background: S.chromeHover } }
  }
});

/**
 * Settings: the site-wide values every page is built with, then the house
 * style for pasted sections. Only what you change is saved.
 */
const SettingsScreen: React.FC = () => {
  const c = useStyles();
  const dispatch = useDispatch();
  // Site settings are for super admins; anyone signed in can work on the house style.
  const isSuperAdmin = useSelector((state: { app: { user?: { isSuperAdmin?: boolean } } }) => !!state.app.user?.isSuperAdmin);
  const { data, loading, error, refetch } = useQuery(SETTINGS, { fetchPolicy: 'cache-and-network', skip: !isSuperAdmin });
  const [update] = useMutation(UPDATE, { refetchQueries: ['getSetting'], awaitRefetchQueries: true });
  const settings: Setting[] = useMemo(() => data?.settings || [], [data]);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const changed = Object.keys(edits).filter(k => edits[k] !== (settings.find(s => s.key === k)?.value ?? ''));
  useEffect(() => {
    if (!changed.length) return undefined;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [changed.length]);

  const save = async () => {
    setSaving(true);
    try {
      await update({ variables: { settings: changed.map((key) => { const s = settings.find(x => x.key === key) as Setting; return { key, label: s.label, value: edits[key] }; }) } });
      setEdits({});
      dispatch(flashMessage(`Saved ${changed.length === 1 ? '1 setting' : `${changed.length} settings`}. Pages get the new values when they’re next deployed.`));
    } catch (e) {
      dispatch(flashMessage((e as Error).message, 'ERROR'));
    } finally {
      setSaving(false);
    }
  };

  let body: React.ReactNode;
  if (error && !data) body = <EmptyState tone="error" title="Couldn’t load settings" action={<Button icon="refresh" onClick={() => refetch()}>Try again</Button>}>{error.message}</EmptyState>;
  else if (loading && !data) body = <EmptyState icon="hourglass_empty" title="Loading…" compact />;
  else if (settings.length === 0) body = <EmptyState icon="tune" title="No site settings" compact>Nothing is set up here yet.</EmptyState>;
  else {
    body = (
      <div className={c.list}>
        {settings.map((s) => {
          const value = edits[s.key] ?? s.value ?? '';
          const isChanged = changed.includes(s.key);
          // Chosen from the saved value, not the edit: switching between input and textarea
          // while typing would remount the field and drop focus mid-word.
          const saved = s.value ?? '';
          const long = saved.length > 80 || saved.includes('\n');
          const id = `setting-${s.key}`;
          return (
            <div key={s.key} className={c.row} data-setting={s.key}>
              <label className={c.label} htmlFor={id}>
                {s.label || s.key}
                <span className={c.key}>{s.key}</span>
              </label>
              {long ? (
                <textarea id={id} className={`${c.input} ${isChanged ? c.changed : ''}`} rows={Math.min(8, value.split('\n').length + 1)} value={value} spellCheck={false}
                  onChange={e => setEdits(x => ({ ...x, [s.key]: e.target.value }))} />
              ) : (
                <input id={id} className={`${c.input} ${isChanged ? c.changed : ''}`} value={value} spellCheck={false}
                  onChange={e => setEdits(x => ({ ...x, [s.key]: e.target.value }))} />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <Page testId="settings-screen">
      <PageHeader title={tr('Settings', '设置')} description="Values used on every page of the site. Changes reach a page the next time it’s deployed." />
      {isSuperAdmin && (
        <Section title="Site" testId="site-settings">
          <Card>{body}</Card>
          {changed.length > 0 && (
            <div className={c.saveBar} role="region" aria-label="Unsaved settings" data-testid="settings-save-bar">
              <span className={c.saveText}>{changed.length === 1 ? '1 unsaved change' : `${changed.length} unsaved changes`}</span>
              <button type="button" className={c.ghost} onClick={() => setEdits({})} disabled={saving}>Discard</button>
              <button type="button" className={c.light} onClick={save} disabled={saving} data-testid="settings-save">{saving ? 'Saving…' : 'Save changes'}</button>
            </div>
          )}
        </Section>
      )}
      <Section title="House style" testId="house-style">
        <Card>
          <HouseStyleEditor />
        </Card>
      </Section>
    </Page>
  );
};

export default SettingsScreen;
